import { jwtDecode as jwt_decode } from "jwt-decode";
import { DateTime } from "luxon";
import { useEffect } from "react";
import { useImmer } from "use-immer";

import { formatMetricsAsTable, VariationInfo } from "@intelligems/ig-analytics";
import {
  AnalyzeTinybirdResourceResponseBodyType,
  ExperienceFe,
  ExperienceStatus,
} from "@intelligems/ig-types";
import { useSyncWorkflow } from "@src/api/ai/execute-workflows/sync-workflow";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { buildMetricDefinitionsMarkdown } from "@src/features/ai/components/usages/metric-definitions-markdown";
import { JwtToken } from "@src/types/user";
import { getAuthToken } from "@src/utils";
import { UseQueryResult } from "@tanstack/react-query";

import { formatAnalyticsSummaryTimestamp } from "./analyticsSummaryFormatters";

export const EXPERIMENT_ANALYTICS_SUMMARY_WORKFLOW_ID =
  "fe3cb72b-ae39-4ff1-bcb6-0494339f5d59";

export const EXPERIMENT_ANALYTICS_SUMMARY_CHAT_WORKFLOW_ID =
  "557366c5-4425-4181-8f3a-758cc60e9396";

/**
 * Optional workflow version override, set on the CLI to test an unpromoted
 * version (`VITE_WF_VERSION_EXPERIMENT_ANALYTICS_SUMMARY=1.2.0 pnpm …`).
 * Undefined in normal builds, so no version is sent and the backend runs
 * whichever one is promoted live. Mirrors VITE_WF_VERSION_MODIFICATIONS_CHAT.
 *
 * Note that forceFresh is only honoured from 1.2.0 on, so the refresh control
 * is inert against an older live version.
 */
const EXPERIMENT_ANALYTICS_SUMMARY_WORKFLOW_VERSION =
  import.meta.env.VITE_WF_VERSION_EXPERIMENT_ANALYTICS_SUMMARY || undefined;

/**
 * How long a summary must have existed before it can be regenerated by hand.
 * Measured against the summary's own age rather than a record of button
 * presses: that age is the workflow cache entry's updatedAt, so the limit
 * survives reloads and applies to everyone sharing the experience's summary.
 */
const REFRESH_COOLDOWN_MS = 5 * 60_000;

/**
 * A concluded experiment always serves its cached summary — the workflow's
 * condition only lets forceFresh override the staleness window, not the
 * paused/ended branches — so the control would be a no-op here.
 */
const isConcludedStatus = (status: ExperienceStatus | null | undefined) =>
  status === "paused" || status === "ended";

interface UseAnalyticsSummaryV2Props {
  experience: ExperienceFe;
  dashboardData: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
}

interface ExperienceSummaryObject {
  title: string;
  summary: string;
  questions: string[];
}

interface CacheLookup {
  hit: boolean;
  value: ExperienceSummaryObject | null;
  createdAt: string | null;
  updatedAt: string | null;
}

interface TestSummaryData extends ExperienceSummaryObject {
  session_id: string;
  timestamp: string;
}

export interface ExperimentChatPayload {
  experience_id: string;
  experienceStatus: ExperienceStatus | null | undefined;
  overall_variation_overview: string;
  metric_definitions: string;
}

export interface V2Summary {
  payload: ExperimentChatPayload;
  data: TestSummaryData;
}

export type V2TestSummaryResponseState =
  | { isPending: boolean; isSuccess: false; data: undefined }
  | { isPending: false; isSuccess: true; data: TestSummaryData };

export const useAnalyticsSummaryV2 = ({
  experience,
  dashboardData,
}: UseAnalyticsSummaryV2Props) => {
  const { mutate: runWorkflow, response, isPending, error } = useSyncWorkflow();
  const [summary, setSummary] = useImmer<V2Summary | null>(null);
  const [copyFeedback, setCopyFeedback] = useImmer(false);
  const [refreshCoolingDown, setRefreshCoolingDown] = useImmer(false);
  const org = useGetOrg();

  const variationOverview = dashboardData.find(
    (metric) => metric.data?.datasetId === "variation_overview"
  );

  const variations: VariationInfo[] = experience.variations.map(
    (variation) => ({
      id: variation.id,
      name: variation.name,
      isControl: variation.isControl,
      order: variation.order,
    })
  );

  const token = getAuthToken();
  const jwt: JwtToken | null = (() => {
    if (!token) return null;
    if (token.startsWith("ShareToken ")) return null;
    if (token.split(".").length !== 3) return null;
    try {
      return jwt_decode(token);
    } catch {
      return null;
    }
  })();

  const hasValidAuth =
    !!jwt &&
    jwt.orgId === org.data.org.id &&
    jwt.shopId === org.data.org.shopId;

  const buildSummaryVariables = (
    data: AnalyzeTinybirdResourceResponseBodyType
  ) => ({
    experienceId: experience.id!,
    experienceStatus: experience.status,
    overall_variation_overview: formatMetricsAsTable(
      data.metrics as Record<string, unknown>[],
      { variations }
    ),
    metric_definitions: buildMetricDefinitionsMarkdown(),
  });

  useEffect(() => {
    if (!hasValidAuth) return;
    if (!variationOverview?.data) return;
    if (summary || isPending || response || error) return;

    void runWorkflow({
      workflowId: EXPERIMENT_ANALYTICS_SUMMARY_WORKFLOW_ID,
      version: EXPERIMENT_ANALYTICS_SUMMARY_WORKFLOW_VERSION,
      initialContext: {
        variables: buildSummaryVariables(variationOverview.data),
      },
    });
  }, [
    hasValidAuth,
    variationOverview,
    summary,
    isPending,
    response,
    error,
    runWorkflow,
    experience.id,
  ]);

  useEffect(() => {
    if (!response || summary) return;

    const cacheLookup = response.variables?.cache_lookup as
      | CacheLookup
      | undefined;

    const freshSummary = response.variables?.experienceSummary as
      | ExperienceSummaryObject
      | undefined;

    const usedCache =
      !freshSummary && !!cacheLookup?.hit && !!cacheLookup.value;
    const experienceSummary = freshSummary ?? cacheLookup?.value ?? undefined;

    if (!experienceSummary) return;
    if (!variationOverview?.data) return;

    const overallVariationOverview = formatMetricsAsTable(
      variationOverview.data.metrics as Record<string, unknown>[],
      { variations }
    );

    setSummary({
      payload: {
        experience_id: experience.id!,
        experienceStatus: experience.status,
        overall_variation_overview: overallVariationOverview,
        metric_definitions: buildMetricDefinitionsMarkdown(),
      },
      data: {
        ...experienceSummary,
        session_id: response.sessionId,
        timestamp:
          usedCache && cacheLookup?.updatedAt
            ? cacheLookup.updatedAt
            : new Date().toISOString(),
      },
    });
  }, [response, summary, setSummary, variationOverview, experience]);

  const testSummaryResponse: V2TestSummaryResponseState =
    summary != null
      ? { isPending: false, isSuccess: true, data: summary.data }
      : { isPending, isSuccess: false, data: undefined };

  const handleCopyToClipboard = () => {
    if (summary?.data.summary) {
      void navigator.clipboard.writeText(summary.data.summary).then(() => {
        setCopyFeedback(true);
        setTimeout(() => setCopyFeedback(false), 2000);
      });
    }
  };

  // The cooldown is held in state rather than recomputed at render, because
  // nothing else re-renders this card when the window elapses. One timer for
  // exactly the remaining time; a slept machine just fires it late, which
  // errs toward enabling the control.
  useEffect(() => {
    const timestamp = summary?.data.timestamp;
    if (!timestamp) {
      setRefreshCoolingDown(false);
      return;
    }

    const remainingMs =
      REFRESH_COOLDOWN_MS -
      (Date.now() - DateTime.fromISO(timestamp).toMillis());
    if (remainingMs <= 0) {
      setRefreshCoolingDown(false);
      return;
    }

    setRefreshCoolingDown(true);
    const timer = setTimeout(() => setRefreshCoolingDown(false), remainingMs);
    return () => clearTimeout(timer);
  }, [summary?.data.timestamp, setRefreshCoolingDown]);

  const isConcluded = isConcludedStatus(experience.status);
  const refreshDisabled =
    !summary || isPending || refreshCoolingDown || isConcluded;

  const refreshTooltip = isConcluded
    ? "This test is finished, so its summary is final"
    : refreshCoolingDown
      ? "Just updated — you can refresh again in a few minutes"
      : "Regenerate summary";

  const handleRefresh = () => {
    if (refreshDisabled) return;
    if (!variationOverview?.data) return;

    // Clearing the summary drops the card to its loading skeleton. It also
    // stops the second effect from re-applying the previous response before
    // the new one lands.
    setSummary(null);

    void runWorkflow({
      workflowId: EXPERIMENT_ANALYTICS_SUMMARY_WORKFLOW_ID,
      version: EXPERIMENT_ANALYTICS_SUMMARY_WORKFLOW_VERSION,
      initialContext: {
        variables: {
          ...buildSummaryVariables(variationOverview.data),
          forceFresh: true,
        },
      },
    });
  };

  return {
    testSummaryResponse,
    summary,
    copyFeedback,
    variationOverview,
    hasValidAuth,
    handleCopyToClipboard,
    handleRefresh,
    refreshDisabled,
    refreshTooltip,
    formatTimestamp: formatAnalyticsSummaryTimestamp,
  };
};
